import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Alert, Box, Grid, TextField, Typography } from "@mui/material";
import { useActualOrderStore } from "../../Store/actualOrderStore";
import { validateSecurityCusip, fetchAccounts, fetchStrategyParameters, fetchRfqStrategyParams, fetchMarketDataDetail, fetchBondCalcSettlementDate } from "../../http/api";
import { inputStyle } from "./styles";
import { fieldOptions, updateVenues, filterVenueOptions, isTradingDeskSelected } from "./optionsHelper";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../Context/AuthProvider";
import useSnackbar from "../../Utils/useSnackbar";
import BondDetailsPanel from "./BondDetailsPanel";
import MarketDataInfoPanel from "./MarketDataInfoPanel";
import { normalizeMarketDataDetail } from "../../Utils/marketDataDetail";
import {
  PROTOCOL_TYPES,
  ORDER_SIDES,
  MKTX_DEALER_LIST_MODES,
  MKTX_INQUIRY_TYPES,
  MKTX_PRICE_TYPES,
  MKTX_STRATEGY_TYPES,
  MKTX_PRICING_PROCESSES,
  TRUMID_PARTIAL_EXECUTION,
  MKTX_DEFAULT_EXPIRY_TIME_UTC
} from "../../Constants/AppConstants";
import { isStagingOrder, stagingOrderFields, liveOrderFields } from "../../Constants/stagingBlotter";
import useBondCalculations from "../../hooks/useBondCalculations";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { fetchFilteredInventory } from "../../http/api";
import { handleViewInventory } from "../../Utils/viewInventoryHandler";
// Imported Modular Components
import OrderPrimaryFields from "./ActualOrderTabOrderPrimaryFields";
import OrderProtocolSection from "./ActualOrderTabOrderProtocolSection";
import OrderComments from "./ActualOrderTabOrderComments";

const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toLocaleString('en-US');
};

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const hasUsablePrice = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === "null" || normalized === "undefined") return false;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0;
};

const parseDisplayDateUTC = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  const valueStr = String(dateValue).trim();
  const ymd = /^\s*(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?\s*$/;
  const ymdCompact = /^\s*(\d{4})(\d{2})(\d{2})\s*$/;
  const mdY = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T\s].*)?\s*$/;
  let match;

  if ((match = valueStr.match(ymd)) || (match = valueStr.match(ymdCompact))) {
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  }

  if ((match = valueStr.match(mdY))) {
    return new Date(Date.UTC(Number(match[3]), Number(match[1]) - 1, Number(match[2])));
  }

  const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(valueStr) ? valueStr : `${valueStr}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};



const ActualOrderTab = ({
  inventoryId = null,
  readOnly,
  qtyPriceEditMode = false,
  accountIdentifiers: propAccountIdentifiers,
  token,
  cusip,
  side,
  qty,
  errors,
  venueOptions,
  onVenueOptionsChange,
  source,
  onCusipValidationChange,
  setBondsDetailsMain,
  showBondsDetails,
  setShowBondsDetails,
  clearModalError,
  secondarySource,
}) => {

  const {
    actualOrderData,
    setActualOrderData,
    clearFieldError,
    accountIdentifiers: storeAccountIdentifiers,
    setCusipError: setStoreCusipError,
    setWarningMessage,
    fetchedMarketData,
    setFetchedMarketData,
    marketDataError,
    setMarketDataError,
  } = useActualOrderStore();
  const { bondCharacteristicData, setCurrentCharacteristicID } = useBondCharacteristicStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isMarketDataLoading, setIsMarketDataLoading] = useState(false);
  const [cusipError, setCusipError] = useState("");
  const [bondDetails, setBondDetails] = useState(null);
  const { token: jwtToken, userId } = useAuth();
  const decodedToken = jwtDecode(jwtToken);

  const { showSuccess, showError, SnackbarComponent } = useSnackbar();
  const [availableVenues, setAvailableVenues] = useState(fieldOptions.venue || []);
  const [venueOptionsLoaded, setVenueOptionsLoaded] = useState(false);
  const [paramLoading, setParamLoading] = useState(false);
  const [paramError, setParamError] = useState(null);
  const [strategyParamsAutoLoaded, setStrategyParamsAutoLoaded] = useState(false);

  // Non-editable fields when editing RFQ
  const nonEditableFields = useMemo(
    () => (actualOrderData.editRfqMode ? ["cusip", "protocol"] : []),
    [actualOrderData.editRfqMode]
  );

  // Allowed editable fields in edit RFQ mode.
  // "side" is deliberately absent: MarketAxess does not let 35=G change Side (54). Spec p.41
  // lists 54 as required on 35=G but not among the fields that "may be amended by 35=G", and
  // UAT 2026-10-28 confirmed it - a BUY->SELL amend was refused with 35=9 "No modifications
  // found for the inquiry" and the order stayed BUY. The side stays visible, locked to its value.
  const editRfqEditableFields = useMemo(
    () =>
      actualOrderData.editRfqMode
        ? [
          "qty",
          "mktxTargetLevel",
          "strategyType",
          "effectiveTime",
          "expiryTime",
          "strategyParameters",
        ]
        : [],
    [actualOrderData.editRfqMode]
  );

  const editRfqParamNames = useMemo(
    () =>
      (actualOrderData.strategyParamDefinitions || []).map((p) => p.paramName),
    [actualOrderData.strategyParamDefinitions]
  );

  // Restore saved MA parameters for viewing, editing and single-bond reinitiation.
  useEffect(() => {
    let cancelled = false;
    const isReinitiate = source === "orderBlotter" && !readOnly && !actualOrderData?.editRfqMode;
    const fetchRfqParams = async () => {
      // Only fetch if:
      // 1. In read-only, edit-RFQ or reinitiate mode
      // 2. Protocol is MarketAxess
      // 3. rfqId exists
      // 4. strategyType exists (to load definitions)
      if (
        (!readOnly && !actualOrderData?.editRfqMode && !isReinitiate) ||
        actualOrderData?.protocol !== PROTOCOL_TYPES.MARKETAXESS ||
        !actualOrderData?.rfqId ||
        !actualOrderData?.strategyType
      ) {
        return;
      }

      // The store survives tab switches. Do not reload over user edits, or restore
      // old-strategy values after the trader selects a different strategy.
      if (isReinitiate && (
        actualOrderData.strategyParamsPrefilledRfqId === actualOrderData.rfqId ||
        (actualOrderData.strategyTypeOriginal &&
          actualOrderData.strategyTypeOriginal !== actualOrderData.strategyType)
      )) return;

      setParamLoading(true);
      setParamError(null);

      try {
        // Fetch both strategy parameter definitions and actual RFQ values
        const [defs, rfqParams] = await Promise.all([
          fetchStrategyParameters(jwtToken || token, actualOrderData.strategyType),
          fetchRfqStrategyParams(jwtToken || token, actualOrderData.rfqId)
        ]);
        if (cancelled) return;

        const normalizeYesNo = (val) => {
          const upper = String(val ?? "").trim().toUpperCase();
          if (upper === "YES" || upper === "Y") return "1";
          if (upper === "NO" || upper === "N") return "0";
          return val;
        };

        // Convert RFQ params array to object for easier lookup (normalize Yes/No)
        const paramValues = {};
        (rfqParams || []).forEach(param => {
          if (param.paramName && param.paramValue !== undefined && param.paramValue !== null) {
            paramValues[param.paramName] = normalizeYesNo(param.paramValue);
          }
        });

        const shouldCaptureOriginalParams =
          !actualOrderData?.strategyParametersOriginal ||
          Object.keys(actualOrderData.strategyParametersOriginal).length === 0;
        const shouldCaptureOriginalType =
          !actualOrderData?.strategyTypeOriginal && !!actualOrderData?.strategyType;

        const updates = {
          strategyParamDefinitions: defs || [],
          strategyParameters: paramValues,
          strategyParamsExisting: rfqParams || [],
          ...(isReinitiate ? { strategyParamsPrefilledRfqId: actualOrderData.rfqId } : {}),
          ...(shouldCaptureOriginalParams ? { strategyParametersOriginal: { ...paramValues } } : {}),
          ...(shouldCaptureOriginalType ? { strategyTypeOriginal: actualOrderData.strategyType } : {}),
        };

        // Set both definitions and values
        setActualOrderData && setActualOrderData(updates);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching RFQ strategy parameters:", err);
        setParamError(err.message || "Unable to load strategy parameters");
        setActualOrderData && setActualOrderData({
          strategyParameters: {},
          strategyParamDefinitions: [],
          strategyParamsExisting: [],
        });
      } finally {
        if (!cancelled) setParamLoading(false);
      }
    };

    fetchRfqParams();
    return () => { cancelled = true; };
  }, [
    source,
    readOnly,
    actualOrderData?.protocol,
    actualOrderData?.rfqId,
    actualOrderData?.strategyType,
    actualOrderData?.editRfqMode,
    jwtToken,
    token,
    setActualOrderData
  ]);

  const normalizeYesNoValue = (val) => {
    const upper = String(val ?? "").trim().toUpperCase();
    if (upper === "YES" || upper === "Y") return "1";
    if (upper === "NO" || upper === "N") return "0";
    return val;
  };

  const mergeStrategyParamDefaults = (definitions = [], existingParams = {}, editRfqMode = false) => {
    if (editRfqMode) return { ...existingParams };
    const nextParams = { ...existingParams };
    (definitions || []).forEach((param) => {
      const key = param.paramName || param.name || param.id;
      if (!key) return;
      const current = nextParams[key];
      if (current !== undefined && current !== null && current !== "") return;
      const defaultValue = normalizeYesNoValue(param.defaultValue);
      if (defaultValue === undefined || defaultValue === null || defaultValue === "") return;
      nextParams[key] = defaultValue;
    });
    return nextParams;
  };

  const loadStrategyParams = useCallback(
    async (strategyTypeValue = actualOrderData?.strategyType, protocolOverride = actualOrderData?.protocol) => {
      const proto = protocolOverride || actualOrderData?.protocol;
      const typeToLoad = strategyTypeValue || actualOrderData?.strategyType;
      const shouldLoad = proto === PROTOCOL_TYPES.MARKETAXESS && typeToLoad;

      // Skip if in read-only mode (params will be fetched by the effect above)
      // UNLESS we are in sendToMarket mode where we need to load them to allow editing
      // Also skip if in editRfqMode as that is handled by the other effect
      if ((readOnly && source !== "sendToMarket") || actualOrderData?.editRfqMode) {
        return;
      }

      if (!shouldLoad) {
        setActualOrderData && setActualOrderData({ strategyParameters: {}, strategyParamDefinitions: [] });
        setParamError(null);
        setParamLoading(false);
        return;
      }
      setParamLoading(true);
      setParamError(null);
      try {
        const defs = await fetchStrategyParameters(jwtToken || token, typeToLoad);
        const isTypeChanged = strategyTypeValue !== undefined && strategyTypeValue !== actualOrderData?.strategyType;
        const baseParams = isTypeChanged ? {} : actualOrderData.strategyParameters || {};
        const strategyParameters = mergeStrategyParamDefaults(defs || [], baseParams, actualOrderData?.editRfqMode);

        setActualOrderData && setActualOrderData({
          strategyParameters,
          strategyParamDefinitions: defs || []
        });
        clearFieldError && clearFieldError("strategyParameters");
      } catch (err) {
        console.error("Error loading strategy parameters:", err);
        setParamError(err.message || "Unable to load strategy parameters");
        setActualOrderData && setActualOrderData({ strategyParameters: {}, strategyParamDefinitions: [] });
      } finally {
        setParamLoading(false);
      }
    },
    [actualOrderData?.protocol, actualOrderData?.strategyType, actualOrderData?.editRfqMode, readOnly, source, jwtToken, token, setActualOrderData, clearFieldError]
  );

  // Auto-load MarketAxess strategy params for source flows that prefill protocol.
  useEffect(() => {
    const shouldAutoLoadStrategyParams =
      source === "sendToMarket" || source === "maInventoryAddOrder";

    if (
      shouldAutoLoadStrategyParams &&
      actualOrderData?.protocol === PROTOCOL_TYPES.MARKETAXESS &&
      actualOrderData?.strategyType &&
      !strategyParamsAutoLoaded
    ) {
      setStrategyParamsAutoLoaded(true);
      loadStrategyParams();
    }
  }, [source, actualOrderData?.protocol, actualOrderData?.strategyType, loadStrategyParams, strategyParamsAutoLoaded]);

  const handleStrategyParamChange = (name, value) => {
    const next = { ...(actualOrderData.strategyParameters || {}) };
    next[name] = value;
    setActualOrderData && setActualOrderData({ strategyParameters: next });
    clearFieldError && clearFieldError("strategyParameters", name);
    clearModalError && clearModalError("strategyParameters");
  };

  const {
    setBondCalcResults,
    bondCalcResults,
    calcLoading,
    calcErrorMessage,
    calculateBondValues,
  } = useBondCalculations({
    cusip: actualOrderData.cusip,
    price: actualOrderData.price,
    userId: userId || decodedToken.userId,
    token: jwtToken || token,
    useDirectResponse: true,
  });

  const calculateBondValuesRef = useRef(calculateBondValues);

  useEffect(() => {
    calculateBondValuesRef.current = calculateBondValues;
  }, [calculateBondValues]);

  // NEW: local state for maturity-related message
  const [maturityMessage, setMaturityMessage] = useState(null);
  const isSendToMarket = () => source === "sendToMarket";
  // "Add Order" on a ladder-linked characteristic starts with no bond, so it has no dealer
  // of its own until a CUSIP resolves. Its Dealer field is editable (see isFieldEditable),
  // so seed it from the verified bond's broker/dealer.
  const isLadderLinkedCharacteristic = () =>
    source === "orderViaCharacteristic" && Boolean(actualOrderData.bondLadderId);

  // Only that flow takes its dealer from the bond. Send-to-market must NOT: the staged row
  // already carries the dealer whose inventory the order came from, and useOrderEntry copies
  // it into the draft when the modal opens. Deriving from the bond here would overwrite that
  // with the secmaster broker/dealer for the cusip - a different dealer, and not the one this
  // order is actually against - every time the prefilled cusip verifies, silently discarding
  // what the trader was shown in the blotter.
  const shouldPrefillDealerFromBond = () => isLadderLinkedCharacteristic();

  // Only accept a bond's broker/dealer if it is one of the dealers this protocol actually
  // offers. Secmaster free-text names regularly are not, and the CLOB Dealer field is a
  // single-select: a value with no matching MenuItem renders blank, so prefilling one would
  // leave the field looking empty while the draft silently carried it. Computed from
  // availableVenues rather than the offeredVenueOptions memo because this runs inside the
  // async CUSIP-verification callbacks, which are defined above that memo.
  const isDealerOffered = (dealer) =>
    Boolean(dealer) &&
    filterVenueOptions({
      venues: availableVenues,
      protocol: actualOrderData.protocol,
    }).includes(dealer);
  const isLadderLevelSendToMarket = secondarySource === "ladder-level-send-to-market";
  const ladderReferenceCusip = actualOrderData?.ladderReferenceCusip || "";
  const ladderReferencePrice = actualOrderData?.ladderReferencePrice ?? "";

  const requestBondCalculation = useCallback((price, cusip) => {
    if (!cusip || String(cusip).trim().length < 9 || !hasUsablePrice(price)) {
      return false;
    }

    const calcKey = `${String(cusip).trim().toUpperCase()}|${String(price).trim()}`;
    if (lastPriceCalcRequestRef.current === calcKey) {
      return false;
    }

    lastPriceCalcRequestRef.current = calcKey;
    calculateBondValuesRef.current(price, cusip);
    return true;
  }, []);

  // Helper: return true if secMasterDTO indicates a maturity date in the past (UTC)
  const isSecMasterMatured = (secMasterOrData) => {
    if (!secMasterOrData) return false;
    const sec = secMasterOrData.secMasterDTO || secMasterOrData;
    const maturityStr =
      (sec && (sec.maturityDate || sec.maturity)) ||
      (secMasterOrData && secMasterOrData.maturity); // fallback if structure differs
    if (!maturityStr) return false;
    // Parse maturity as UTC midnight
    const maturityDate = new Date(`${maturityStr}T00:00:00Z`);
    if (Number.isNaN(maturityDate.getTime())) return false;

    // NEW: compute current UTC date at 00:00:00 (midnight) to compare date-only (UTC)
    const todayUtcMidnight = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00Z");

    // matured only if maturity date is strictly before today's UTC date
    return maturityDate.getTime() < todayUtcMidnight.getTime();
  };

  const effectiveAccountIdentifiers = propAccountIdentifiers;

  useEffect(() => {}, [
    actualOrderData.pmId,
    effectiveAccountIdentifiers,
    actualOrderData.account,
  ]);

  useEffect(() => {
    if (
      effectiveAccountIdentifiers?.length > 0 &&
      !actualOrderData.account &&
      !readOnly
    ) {
      setActualOrderData({
        ...actualOrderData,
        account: effectiveAccountIdentifiers[0],
      });
    }
  }, [effectiveAccountIdentifiers, actualOrderData.account, readOnly, setActualOrderData]);

  useEffect(() => {
    if (cusip && (!actualOrderData.cusip || actualOrderData.cusip !== cusip)) {
      setActualOrderData({ ...actualOrderData, cusip });
    }
    if (side && (!actualOrderData.side || actualOrderData.side !== side)) {
      setActualOrderData({ ...actualOrderData, side });
    }
    if (qty && (!actualOrderData.qty || actualOrderData.qty !== qty)) {
      setActualOrderData({ ...actualOrderData, qty });
    }
  }, [cusip, side, qty, setActualOrderData, actualOrderData]);

  // Keep freshest values without re-triggering the CUSIP effect
  const latestTokenRef = useRef(token);
  const latestInventoryIdRef = useRef(inventoryId);
  const lastCharacteristicCheckRef = useRef(null);
  const lastPriceCalcRequestRef = useRef("");
  const cusipValidationRequestRef = useRef(0);
  const marketDataRequestRef = useRef(0);
  const settlementDateFetchTokenRef = useRef(null);
  // Read by the "already validated this identifier" guard below, which must know whether the
  // details that validation produced are still in hand. Mirrored into a ref like the two
  // above rather than added to the effect's dependencies on purpose: bondDetails changes on
  // every successful validation, and re-running the effect there would restart its debounce
  // and re-clear the market data it had just fetched.
  const bondDetailsRef = useRef(null);

  useEffect(() => { latestTokenRef.current = token; }, [token]);
  useEffect(() => { latestInventoryIdRef.current = inventoryId; }, [inventoryId]);
  useEffect(() => { bondDetailsRef.current = bondDetails; }, [bondDetails]);

  useEffect(() => {
    const validateCusip = async () => {
      if (isLadderLevelSendToMarket) {
        setCusipError("");
        setStoreCusipError("");
        setBondDetails(null);
        setFetchedMarketData(null);
        setMarketDataError(null);
        setIsMarketDataLoading(false);
        if (!ladderReferenceCusip || !hasUsablePrice(ladderReferencePrice)) {
          setBondCalcResults(null);
          lastPriceCalcRequestRef.current = "";
        }
        onCusipValidationChange(true);
        return;
      }

      if (actualOrderData.cusip && actualOrderData.cusip.length >= 9) {
        const validationRequestId = ++cusipValidationRequestRef.current;
        marketDataRequestRef.current += 1;
        setIsLoading(true);
        setCusipError("");
        setStoreCusipError("");
        setFetchedMarketData(null);
        setMarketDataError(null);
        setIsMarketDataLoading(false);
        if (source !== "orderViaCharacteristic") {
          setWarningMessage(null);
        }
        try {
          const authToken = latestTokenRef.current;
          const invId = latestInventoryIdRef.current;
          let data;
          // Don't validate the same identifier twice - but only while we still have what the
          // last validation gave us.
          //
          // In plain terms: "have I already checked this CUSIP, AND do I still have the
          // result?" It used to ask only the first half, and that was the bug. cusipRef is
          // written on a successful validation (~line 535) and lives in actualOrderStore, so
          // it SURVIVES this tab being unmounted - which happens on every sub-tab switch,
          // because TabPanel renders `{value === index && children}` (OrderEntryCore ~line
          // 46). bondDetails is plain component state and does not survive. So on coming
          // back the marker still said "already done" while the data behind it was gone, and
          // this returned before validateSecurityCusip and before the market data fetch
          // further down - leaving a validated-looking identifier with no details, no bond
          // calculations and no market data behind it.
          //
          // Reading bondDetailsRef rather than bondDetails so this sees the current value
          // rather than whatever the closure captured when the effect last ran.
          //
          // Nothing changes while the tab stays mounted: the case-normalising write below
          // (setActualOrderData({ cusip: data.cusip })) re-runs this effect with the details
          // already set, so that second pass still stops here instead of fetching again.
          if (actualOrderData.cusipRef !== '' && actualOrderData.cusipRef !== null && actualOrderData.cusipRef !== undefined) {
            if (
              bondDetailsRef.current &&
              actualOrderData.cusipRef.toLowerCase() === actualOrderData.cusip.toLowerCase()
            ) {
              setActualOrderData({ cusip: actualOrderData.cusipRef });
              return;
            }
          }
          if (invId) {
            data = await validateSecurityCusip(authToken, actualOrderData.cusip);
            if (validationRequestId !== cusipValidationRequestRef.current) return;
            setActualOrderData({ bondInventoryId: invId });
          } else {
            data = await validateSecurityCusip(authToken, actualOrderData.cusip);
            if (validationRequestId !== cusipValidationRequestRef.current) return;
            const isManualTraded = readOnly && actualOrderData.rfqStatus === "MANUAL_TRADED";
            if (data.success === false) {
              const errorMsg = data.message || "Invalid Identifier";
              if (source === "allocateAccount") {
                setBondDetails(null);
                setBondCalcResults(null);
                lastPriceCalcRequestRef.current = "";
                onCusipValidationChange(true);
                setShowBondsDetails(true);
                return;
              }
              setCusipError(errorMsg);
              setStoreCusipError(errorMsg);
              setBondDetails(null);
              setBondCalcResults(null);
              lastPriceCalcRequestRef.current = "";
              if(!isManualTraded)onCusipValidationChange(false); 
              else {
                onCusipValidationChange(true);
              };
              setActualOrderData({ cusipRef: '' });
              if (actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ && !isManualTraded) {
                setActualOrderData({ protocol: PROTOCOL_TYPES.CLOB });
                clearTruMidFields();
              }
              return;
            }
            if (data && data.id) {
              setActualOrderData({cusip : data.cusip}); 
              setActualOrderData({ cusipRef: data.cusip || '' });
              setActualOrderData({ bondInventoryId: data.id });
            }
          }

          if (source === "inventory" && data && data.minQty != null) {
            const minQtyVal = data.minQty;
            setActualOrderData({ minQty: minQtyVal });
            if (!actualOrderData.qty || Number(actualOrderData.qty) < Number(minQtyVal)) {
              setActualOrderData({ qty: `${minQtyVal}` });
              clearFieldError && clearFieldError("qty");
            }
          }
          setBondDetails(data);
          const isManualTraded = readOnly && actualOrderData.rfqStatus === "MANUAL_TRADED";
          const isCorporateFromData = (() => {
            const sec = data?.secMasterDTO || data;
            const bondTypeRaw = (sec && (sec.bondType || sec.type)) || "";
            return typeof bondTypeRaw === "string" && bondTypeRaw.toLowerCase().includes("corp");
          })();
          if (!isManualTraded && !isCorporateFromData && actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
            setActualOrderData({ protocol: PROTOCOL_TYPES.CLOB });
            clearTruMidFields();
            clearMarketAxessFields({});
          }

          const matured = isSecMasterMatured(data);
          if (matured) {
            setMaturityMessage("This bond has already matured (maturity date is before today). Bond calculations are not available.");
            setBondCalcResults(null);
            lastPriceCalcRequestRef.current = "";
            onCusipValidationChange(true);
            const brokerDealer = data && (data.brokerDealer || data.broker || data.brokerDealerName);
            if (shouldPrefillDealerFromBond() && isDealerOffered(brokerDealer)) {
              setActualOrderData({ venue: brokerDealer });
              clearFieldError && clearFieldError("venue");
            }
          } else {
            setMaturityMessage(null);
          }

          if (source === "maInventoryAddOrder") {
            setActualOrderData({
              qty: "",
              price: null,
            });
            setBondCalcResults(null);
            lastPriceCalcRequestRef.current = "";
          } else if (hasUsablePrice(data.price) && !readOnly) {
            setActualOrderData({ price: `${data.price}` });
            if (!matured && actualOrderData.cusip && actualOrderData.cusip.length >= 9) {
              requestBondCalculation(data.price, actualOrderData.cusip);
            }
          } else {
            setBondDetails(data);
            if (
              !matured &&
              actualOrderData.cusip &&
              actualOrderData.cusip.length >= 9 &&
              hasUsablePrice(actualOrderData.price)
            ) {
              requestBondCalculation(actualOrderData.price, actualOrderData.cusip);
            }
          }
          const brokerDealer = data && (data.brokerDealer || data.broker || data.brokerDealerName);
          if (shouldPrefillDealerFromBond() && isDealerOffered(brokerDealer)) {
            setActualOrderData({ venue: brokerDealer });
            clearFieldError && clearFieldError("venue");
          }
          setBondDetails(data);
          onCusipValidationChange(true);
          setStoreCusipError("");
          const cusipVal = data?.cusip || actualOrderData.cusip;
          if (cusipVal) {
            const currentMarketDataRequestId = ++marketDataRequestRef.current;
            setIsMarketDataLoading(true);
            fetchMarketDataDetail(latestTokenRef.current, cusipVal)
              .then((mdResponse) => {
                if (
                  validationRequestId !== cusipValidationRequestRef.current ||
                  currentMarketDataRequestId !== marketDataRequestRef.current
                ) {
                  return;
                }

                const normalizedMarketData = normalizeMarketDataDetail(mdResponse);
                if (normalizedMarketData) {
                  setMarketDataError(null);
                  setFetchedMarketData(normalizedMarketData);
                } else {
                  setFetchedMarketData(null);
                  setMarketDataError(mdResponse?.message || "Market data is not available for this security.");
                }
                setIsMarketDataLoading(false);
              })
              .catch(() => {
                if (
                  validationRequestId !== cusipValidationRequestRef.current ||
                  currentMarketDataRequestId !== marketDataRequestRef.current
                ) {
                  return;
                }

                setFetchedMarketData(null);
                setMarketDataError("Something went wrong while fetching market data. Please try again.");
                setIsMarketDataLoading(false);
              });
          }
          // NEW: For characteristic order placement, validate CUSIP against characteristic filters
          if (source === "orderViaCharacteristic") {
            try {
              const characteristicId = bondCharacteristicData?.characteristicId || "";
              const cusipValue = (data?.cusip || actualOrderData.cusip || "").trim();
              const checkKey = `${cusipValue}|${characteristicId}`;

              if (cusipValue && lastCharacteristicCheckRef.current !== checkKey) {
                lastCharacteristicCheckRef.current = checkKey;
                setWarningMessage(null);

                // Only the filter payload is wanted here — this runs on every CUSIP
                // validation, and letting it write order data would re-stamp account
                // from the characteristic each time, wiping the prefilled or
                // user-chosen account.
                const statePayload = handleViewInventory(
                  1,
                  bondCharacteristicData,
                  setCurrentCharacteristicID,
                  () => {},
                  null,
                  showError,
                  null,
                  false
                );

                if (!statePayload) {
                  return;
                }

                const { source: _source, ...filterPayload } = statePayload || {};
                const payload = {
                  ...filterPayload,
                  cusip: cusipValue,
                  page: 0,
                  size: 20,
                };

                const matchResponse = await fetchFilteredInventory(
                  latestTokenRef.current,
                  payload
                );

                if (matchResponse?.totalElements === 0) {
                  setWarningMessage(
                    "Warning: This CUSIP does not match the current characteristic filters."
                  );
                } else {
                  setWarningMessage(null);
                }
              }
            } catch (filterError) {
              console.error("Error validating CUSIP against characteristics:", filterError);
            }
          }
        } catch (error) {
          console.error("Error validating CUSIP:", error);
          if (validationRequestId !== cusipValidationRequestRef.current) return;
          const errorMsg = "Invalid Identifier or error fetching bond details";
          if (source === "allocateAccount") {
            setCusipError("");
            setStoreCusipError("");
            setBondDetails(null);
            onCusipValidationChange(true);
            setShowBondsDetails(true);
          } else {
            setCusipError(errorMsg);
            setStoreCusipError(errorMsg);
            setBondDetails(null);
            setBondCalcResults(null);
            lastPriceCalcRequestRef.current = "";
            onCusipValidationChange(false);
          }
          setWarningMessage(null);
          setFetchedMarketData(null);
          setMarketDataError(null);
          setIsMarketDataLoading(false);
          setActualOrderData({ cusipRef: '' });
          if (actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
            setActualOrderData({ protocol: PROTOCOL_TYPES.CLOB });
            clearTruMidFields();
          }
        } finally {
          if (validationRequestId === cusipValidationRequestRef.current) {
            setIsLoading(false);
          }
        }
      } else if (actualOrderData.cusip) {
        cusipValidationRequestRef.current += 1;
        const errorMsg = "Identifier must be at least 9 characters";
        if (source === "allocateAccount") {
          setCusipError("");
          setStoreCusipError("");
          setBondDetails(null);
          setBondCalcResults(null);
          lastPriceCalcRequestRef.current = "";
          onCusipValidationChange(true);
        } else {
          setCusipError(errorMsg);
          setStoreCusipError(errorMsg);
          setBondDetails(null);
          setBondCalcResults(null);
          lastPriceCalcRequestRef.current = "";
          onCusipValidationChange(false);
        }
        setWarningMessage(null);
        marketDataRequestRef.current += 1;
        setFetchedMarketData(null);
        setMarketDataError(null);
        setIsMarketDataLoading(false);

        setActualOrderData({ cusipRef: '' });
        if (actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
          setActualOrderData({ protocol: PROTOCOL_TYPES.CLOB });
          clearTruMidFields();
        }
      } else {
        cusipValidationRequestRef.current += 1;
        marketDataRequestRef.current += 1;
        setCusipError("");
        setStoreCusipError("");
        setBondDetails(null);
        setBondCalcResults(null);
        setFetchedMarketData(null);
        setMarketDataError(null);
        setIsMarketDataLoading(false);
        lastPriceCalcRequestRef.current = "";
      }
    };
    if (isLadderLevelSendToMarket) {
      validateCusip();
      return undefined;
    }

    const debounceTimeout = setTimeout(() => {
      if (actualOrderData.cusip) validateCusip();
    }, 500);
    return () => clearTimeout(debounceTimeout);
  }, [actualOrderData.cusip, isLadderLevelSendToMarket, ladderReferenceCusip, ladderReferencePrice]);

  useEffect(() => {
    const authToken = jwtToken || token;
    const shouldFetchSettlementDate =
      authToken &&
      (settlementDateFetchTokenRef.current !== authToken || !actualOrderData?.settlementDate);

    if (!shouldFetchSettlementDate) return undefined;

    let cancelled = false;
    settlementDateFetchTokenRef.current = authToken;

    const loadSettlementDate = async () => {
      const settlementDate = await fetchBondCalcSettlementDate(authToken);
      if (cancelled) return;
      setActualOrderData({ settlementDate });
    };

    loadSettlementDate();

    return () => {
      cancelled = true;
    };
  }, [
    jwtToken,
    token,
    actualOrderData?.settlementDate,
    setActualOrderData,
  ]);

  useEffect(() => {
    if (
      source !== "inventory" &&
      source !== "buyOrderEntry" &&
      effectiveAccountIdentifiers?.length > 0 &&
      !actualOrderData.account &&
      !readOnly
    ) {
      setActualOrderData({
        ...actualOrderData,
        account: effectiveAccountIdentifiers[0],
      });
    }
  }, [effectiveAccountIdentifiers]);

  useEffect(() => {
    const priceForCalc = isLadderLevelSendToMarket
      ? ladderReferencePrice
      : actualOrderData.price;

    if (hasUsablePrice(priceForCalc)) return;

    setBondCalcResults(null);
    lastPriceCalcRequestRef.current = "";
  }, [
    actualOrderData.price,
    isLadderLevelSendToMarket,
    ladderReferencePrice,
    setBondCalcResults,
  ]);

  useEffect(() => {
    if (source !== "maInventoryAddOrder") return;

    const topMarketData = fetchedMarketData?.top;
    const isBuySide = actualOrderData.side === ORDER_SIDES.BUY;
    const nextQty = isBuySide ? topMarketData?.bidQuantity : topMarketData?.offerQuantity;
    const nextPrice = isBuySide ? topMarketData?.bidPrice : topMarketData?.offerPrice;

    if (!topMarketData) {
      setActualOrderData({ qty: "", price: null });
      setBondCalcResults(null);
      lastPriceCalcRequestRef.current = "";
      return;
    }

    const updates = {
      qty: hasValue(nextQty) ? nextQty : "",
      price: hasUsablePrice(nextPrice) ? `${nextPrice}` : null,
    };

    setActualOrderData(updates);

    if (!hasUsablePrice(nextPrice) || isSecMasterMatured(bondDetails)) {
      setBondCalcResults(null);
      lastPriceCalcRequestRef.current = "";
      return;
    }

    if (actualOrderData.cusip && actualOrderData.cusip.length >= 9) {
      requestBondCalculation(nextPrice, actualOrderData.cusip);
    }
  }, [
    source,
    fetchedMarketData,
    actualOrderData.side,
    actualOrderData.cusip,
    bondDetails,
    requestBondCalculation,
    setActualOrderData,
    setBondCalcResults,
  ]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    let isValid = true;
    if (name === "qty" && value > 10000000) isValid = false;
    if (name === "price" && value > 150) isValid = false;
    if (name === "ttl" && value > 3000) isValid = false;
    if (isValid) {
      const updates = { [name]: value };

      if (name === "side" && source === "maInventoryAddOrder" && !fetchedMarketData?.top) {
        updates.qty = "";
        updates.price = null;
        setBondCalcResults(null);
        lastPriceCalcRequestRef.current = "";
      }

      // NEW: Clear mktxDealerListMode and venue when switching to SEEK or PROVIDE
      // Restore mktxDealerListMode when switching to TAKE
      if (name === "strategyType" && actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
        if (value === MKTX_STRATEGY_TYPES.SEEK || value === MKTX_STRATEGY_TYPES.PROVIDE) {
          updates.mktxDealerListMode = null;
          updates.venue = [];
        } else if (value === MKTX_STRATEGY_TYPES.TAKE) {
          // Restore default dealer list mode when switching back to TAKE
          updates.mktxDealerListMode = MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST;
          // Clear venue if it was previously set to empty array for SEEK/PROVIDE
          if (Array.isArray(actualOrderData.venue) && actualOrderData.venue.length === 0) {
            updates.venue = [];
          }
        }
        loadStrategyParams(value, PROTOCOL_TYPES.MARKETAXESS);
      }

      setActualOrderData({ ...actualOrderData, ...updates });
      if (value !== undefined && value !== "") {
        clearFieldError(name);
        clearModalError && clearModalError(name);
      }
      if (updates.qty !== undefined && updates.qty !== "") {
        clearFieldError("qty");
        clearModalError && clearModalError("qty");
      }
      if (updates.price !== undefined && updates.price !== "") {
        clearFieldError("price");
        clearModalError && clearModalError("price");
      }
      if (name === "cusip" && value.length >= 9) {
        clearFieldError("cusip");
        clearModalError && clearModalError("cusip");
      }
    }
  };

  const handleProtocolChange = (event) => {
    const newProtocol = event.target.value;
    if (newProtocol !== PROTOCOL_TYPES.MARKETAXESS) setStrategyParamsAutoLoaded(false);

    const updates = {
      protocol: newProtocol,
      venue: newProtocol === PROTOCOL_TYPES.CLOB
        ? actualOrderData.venue && !Array.isArray(actualOrderData.venue)
          ? actualOrderData.venue
          : ""
        : [],
      ttl: newProtocol === PROTOCOL_TYPES.RFQ ? 300 : null,
    };

    if (newProtocol === PROTOCOL_TYPES.TRUMID_RFQ) {
      updates.venue = [];
      updates.quoteSubmissionDeadline = 5;
      updates.ttl = null;
      updates.rfqInquiryType = MKTX_INQUIRY_TYPES.ASAP;
      updates.priceType = MKTX_PRICE_TYPES.PRICE;
      updates.allowPartialExecution = TRUMID_PARTIAL_EXECUTION.YES;
      clearMarketAxessFields(updates);
    } else if (newProtocol === PROTOCOL_TYPES.MARKETAXESS) {
      updates.venue = [];
      updates.mktxDealerListMode = MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST;
      updates.rfqInquiryType = MKTX_INQUIRY_TYPES.BIN;
      updates.priceType = MKTX_PRICE_TYPES.SPREAD;
      updates.mktxPricingProcess = MKTX_PRICING_PROCESSES.ONESTEP;
      updates.stipulationType = null;
      updates.allowPartialExecution = null;
      updates.quoteSubmissionDeadline = null;
      updates.strategyType = MKTX_STRATEGY_TYPES.TAKE;

      // NEW: If default strategy is SEEK or PROVIDE, clear mktxDealerListMode
      if (updates.strategyType === MKTX_STRATEGY_TYPES.SEEK || updates.strategyType === MKTX_STRATEGY_TYPES.PROVIDE) {
        updates.mktxDealerListMode = null;
      }

      // Fix: Use MKTX_DEFAULT_EXPIRY_TIME_UTC constant for expiry default, current UTC for effective
      const now = new Date();
      const [timePart] = (MKTX_DEFAULT_EXPIRY_TIME_UTC || "20:57:00.000").split(".");
      const [hh, mm, ss] = timePart.split(":").map(Number);
      const msStr = (MKTX_DEFAULT_EXPIRY_TIME_UTC || "20:57:00.000").split(".")[1] || "0";
      const expiryDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hh, mm, ss, Number(msStr)));
      updates.effectiveTime = now.toISOString().slice(0, -1);
      updates.expiryTime = expiryDate.toISOString().slice(0, -1);
      loadStrategyParams(updates.strategyType, newProtocol);
    } else {
      updates.rfqInquiryType = null;
      updates.priceType = null;
      updates.allowPartialExecution = null;
      updates.quoteSubmissionDeadline = null;
      clearMarketAxessFields(updates);
    }
    setActualOrderData({ ...actualOrderData, ...updates });
    if (newProtocol) clearFieldError("protocol");
  };

  const clearMarketAxessFields = (updates) => {
    updates.mktxDealerListMode = null;
    updates.mktxTargetLevel = null;
    updates.mktxPricingProcess = null;
    updates.strategyType = null;
    updates.effectiveTime = null;
    updates.expiryTime = null;
    updates.strategyParameters = {};
    updates.strategyParamDefinitions = [];
  };

  const handleVenueChange = (event) => {
    const { value } = event.target;
    // Choosing a dealer in the send-to-market modal is what takes an order out of the
    // Staging Blotter, so record that here rather than leaving the draft staged. The
    // submit gates read this to tell an unconfirmed prefill (the order's own inventory
    // dealer, carried over) apart from a dealer the trader actually picked.
    //
    // Trading Desk is the exception: it is offered in the list but means "keep this
    // staged", so it must NOT mark the draft live. The submit gate then refuses it and
    // shows TRADING_DESK_SUBMIT_WARNING.
    const goesLive = source === "sendToMarket" && !isTradingDeskSelected(value);
    setActualOrderData(
      goesLive
        ? { ...actualOrderData, ...liveOrderFields(value) }
        : { ...actualOrderData, venue: value }
    );
    if (value && (typeof value === "string" || value.length > 0)) {
      clearFieldError("venue");
      clearModalError && clearModalError("venue");
    }
  };

  const formatVenues = (venues) => {
    if (!venues) return "";
    if (typeof venues === "string") return venues;
    const venueString = venues.join(", ");
    return venueString.length > 25
      ? venueString.substring(0, 22) + "..."
      : venueString;
  };


  const isFieldEditable = (fieldName) => {
    if (readOnly === "allocate") return fieldName === "account";
    if (source === "maInventoryAddOrder" && fieldName === "protocol") return false;
    if (actualOrderData.editRfqMode) {
      const allowed = new Set(editRfqEditableFields);
      if (allowed.has(fieldName)) return true;
      if (editRfqParamNames.includes(fieldName)) return true;
      return false;
    }
    // If we're in qty-price-edit mode, allow qty and price to be edited
    if (qtyPriceEditMode && (fieldName === "qty" || fieldName === "price")) return true;
    if (isSendToMarket()) {
      if (actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
        const paramNames = (actualOrderData.strategyParamDefinitions || []).map((p) => p.paramName);
        if (fieldName === "strategyType" || fieldName === "effectiveTime" || fieldName === "expiryTime" || paramNames.includes(fieldName)) {
          return true;
        }
      }
      return (
        fieldName === "price" || fieldName === "venue" || fieldName === "protocol" || fieldName === "ttl" || fieldName === "qty" ||
        fieldName === "rfqInquiryType" || fieldName === "quoteSubmissionDeadline" || fieldName === "priceType" || fieldName === "stipulationType" ||
        fieldName === "allowPartialExecution" || fieldName === "mktxDealerListMode" || fieldName === "mktxTargetLevel" || fieldName === "mktxInquiryType" || fieldName === "mktxPriceType" || fieldName === "mktxPricingProcess"
      );
    }
    // An order raised against a characteristic inherits its side: from the characteristic
    // itself on "Add Order", or from the inventory row being bought/sold. Either way the
    // side is decided before the form opens, so it is not the user's to change. Orders with
    // no characteristic behind them keep whatever editability they had. Requires a side to
    // actually be present — locking an empty one would leave no way to submit at all.
    if (fieldName === "side" && actualOrderData.characteristicId && actualOrderData.side) {
      return false;
    }
    // "Add Order" on a ladder-linked characteristic always goes over CLOB, so protocol is
    // not the user's to choose. The Dealer field IS editable here: the order still routes
    // to the Staging Blotter (the staging flag decides that, not the venue), so the dealer
    // records which inventory the order came from and the trader may correct it. It is
    // prefilled from the bond on CUSIP verification (this flow only - send-to-market keeps
    // the dealer its blotter row already carries).
    // Deliberately keyed on source, not on bondLadderId alone: a stale bondLadderId can
    // linger in the order store after viewing a ladder order, and must not lock unrelated
    // order entry.
    if (
      source === "orderViaCharacteristic" &&
      actualOrderData.bondLadderId &&
      fieldName === "protocol"
    ) {
      return false;
    }
    // Add special handling for inventory source
    if (source === "inventory" || source === "buyOrderEntry") {
      return !(fieldName === "venue" || fieldName === "protocol") && !readOnly;
    }
    return !readOnly;
  };

  // Handlers for comments focus and renderAccountField have been moved to child components.
  // Removing unused code blocks to fix linting errors.

  useEffect(() => {
    let cancelled = false;
    const fetchVenues = async () => {
      setVenueOptionsLoaded(false);
      const venues = await updateVenues(jwtToken || token, actualOrderData.protocol);
      if (cancelled) return;
      setAvailableVenues(venues || []);
      setVenueOptionsLoaded(true);
    };
    fetchVenues();
    return () => {
      cancelled = true;
    };
  }, [jwtToken, token, actualOrderData.protocol]);

  // The dealers the Dealer dropdown can actually offer for this protocol.
  const offeredVenueOptions = useMemo(
    () =>
      filterVenueOptions({
        venues: availableVenues,
        protocol: actualOrderData.protocol,
      }),
    [availableVenues, actualOrderData.protocol, source]
  );

  // Report them upward so the submit gate judges the selected dealer against exactly what
  // this dropdown offers, instead of keeping its own copy of the rules.
  useEffect(() => {
    if (!onVenueOptionsChange) return;
    onVenueOptionsChange({ options: offeredVenueOptions, loaded: venueOptionsLoaded });
  }, [offeredVenueOptions, venueOptionsLoaded, onVenueOptionsChange]);

  // Add Order on a ladder-linked characteristic is compulsorily in the Staging Blotter,
  // over CLOB. Pin both, because the fields are disabled: a stale protocol carried over
  // from a previous order would otherwise be locked in and fail validation unrecoverably.
  // Scoped to this one source so no other flow's venue/protocol is touched.
  useEffect(() => {
    if (source !== "orderViaCharacteristic" || !actualOrderData.bondLadderId) return;
    const updates = {};
    if (!isStagingOrder(actualOrderData)) {
      Object.assign(updates, stagingOrderFields(actualOrderData.venue));
    }
    if (actualOrderData.protocol !== PROTOCOL_TYPES.CLOB) {
      updates.protocol = PROTOCOL_TYPES.CLOB;
    }
    if (Object.keys(updates).length > 0) setActualOrderData(updates);
  }, [
    actualOrderData.bondLadderId,
    actualOrderData.venue,
    actualOrderData.protocol,
    source,
    setActualOrderData,
  ]);

  const handlePriceBlur = useCallback(() => {
    // Only calculate if we have valid CUSIP and price and bond not matured
    if (
      bondDetails &&
      actualOrderData.cusip &&
      actualOrderData.cusip.length >= 9 &&
      hasUsablePrice(actualOrderData.price)
    ) {
      if (isSecMasterMatured(bondDetails)) {
        setMaturityMessage(
          "This bond has already matured (maturity date is before today). Bond calculations are not available."
        );
        setBondCalcResults(null);
        // return;
      }
      // clear any previous maturity message
      setMaturityMessage(null);
      requestBondCalculation(actualOrderData.price, actualOrderData.cusip);
    }
  }, [requestBondCalculation, actualOrderData.cusip, actualOrderData.price, bondDetails]);

  // Trigger bond calc when price changes so MA Inventory prefills/edits recalculate immediately.
  useEffect(() => {
    if (source !== "maInventoryAddOrder") return;
    if (!bondDetails) return;
    if (!actualOrderData.cusip || actualOrderData.cusip.length < 9) return;
    if (!hasUsablePrice(actualOrderData.price)) return;

    if (isSecMasterMatured(bondDetails)) {
      setMaturityMessage(
        "This bond has already matured (maturity date is before today). Bond calculations are not available."
      );
      setBondCalcResults(null);
      return;
    }

    const calcKey = `${String(actualOrderData.cusip).trim().toUpperCase()}|${String(actualOrderData.price).trim()}`;
    if (lastPriceCalcRequestRef.current === calcKey) return;

    const debounceTimer = setTimeout(() => {
      setMaturityMessage(null);
      requestBondCalculation(actualOrderData.price, actualOrderData.cusip);
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [
    source,
    actualOrderData.cusip,
    actualOrderData.price,
    bondDetails,
    requestBondCalculation,
    setBondCalcResults,
  ]);

  const filterVenues = (venues) => {
    return filterVenueOptions({
      venues,
      protocol: actualOrderData.protocol,
    });
  };

  useEffect(() => {
    if (
      source === "inventory" ||
      source === "buyOrderEntry"
    ) {
      if (actualOrderData.side || actualOrderData.action) {
        setActualOrderData({
          ...actualOrderData,
          side: actualOrderData.side || actualOrderData.action
        });
      }
    }
  }, [source]);

  useEffect(() => {
    setBondsDetailsMain(bondDetails);
  }, [bondDetails, setBondsDetailsMain]);

  const formatSettlementDate = (dateStr) => {
    const date = parseDisplayDateUTC(dateStr);
    if (!date) return "---";

    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const yyyy = date.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Compute label's min qty, only if defined and valid
  const minQtyForLabel = (() => {
    const n = Number(actualOrderData?.minQty);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  // Show bracket only when source is inventory and CUSIP is validated (bondDetails present and no cusipError)
  const showMinInLabel = source === "inventory" && Boolean(bondDetails && !cusipError) && minQtyForLabel !== null;

  // NEW: centralize venue disabled state so label adopts disabled styling
  const venueDisabled =
    !isFieldEditable("venue") 
    // ||
    // actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ;
  // Check if MarketAxess protocol for qty label hint
  const isMarketAxess = actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS;

  // Helper to detect corporate bonds (normalizes various shapes)
  const isBondCorporate = () => {
    const sec = bondDetails?.secMasterDTO || bondDetails || {};
    const bondTypeRaw =
      (isLadderLevelSendToMarket && actualOrderData?.bondType) ||
      (sec && (sec.bondType || sec.type )) ||
      "";
    if (!bondTypeRaw || typeof bondTypeRaw !== "string") return false;
    return bondTypeRaw.toLowerCase().includes("corp");
  };

  // NEW: helper to clear TruMid RFQ specific fields
  const clearTruMidFields = () => {
    setActualOrderData({
      rfqInquiryType: null,
      priceType: null,
      stipulationType: null,
      allowPartialExecution: null,
      quoteSubmissionDeadline: null,
    });
  };

  // NEW: Check if MANUAL_TRADED to bypass corporate check
  const isManualTraded = readOnly && actualOrderData.rfqStatus === "MANUAL_TRADED";

  const showTrumidOption = isManualTraded || isBondCorporate();
  // MarketAxess is now allowed for all bond types.
  const showMarketAxessOption = true;
  const shouldShowMarketDataPanel =
    !isLadderLevelSendToMarket &&
    Boolean(actualOrderData.cusip) &&
    actualOrderData.cusip.length >= 9 &&
    !cusipError;

  return (
    <Box>
      <SnackbarComponent />
      <Grid container spacing={2}>

        {/* Modularized Primary Inputs */}
        <OrderPrimaryFields
          actualOrderData={actualOrderData}
          errors={errors}
          isFieldEditable={isFieldEditable}
          handleChange={handleChange}
          handleProtocolChange={handleProtocolChange}
          handleVenueChange={handleVenueChange}
          handlePriceBlur={handlePriceBlur}
          isLoading={isLoading}
          calcLoading={calcLoading}
          cusipError={cusipError}
          source={source}
          readOnly={readOnly}
          effectiveAccountIdentifiers={effectiveAccountIdentifiers}
          showTrumidOption={showTrumidOption}
          showMarketAxessOption={showMarketAxessOption}
          showMinInLabel={showMinInLabel}
          minQtyForLabel={minQtyForLabel}
          isMarketAxess={isMarketAxess}
          venueDisabled={venueDisabled}
          fieldOptions={fieldOptions}
          filterVenues={filterVenues}
          formatVenues={formatVenues}
          formatNumberWithCommas={formatNumberWithCommas}
          bondDetails={bondDetails}
          isManualTraded={isManualTraded}
          secondarySource={secondarySource}
        />

        {/* Read-only status fields remain here as they are small */}
        {readOnly && source !== "sendToMarket" && (
          <>
            <Grid item xs={3}>
              <TextField
                fullWidth variant="filled" label="Status" value={actualOrderData.status || ""} disabled sx={inputStyle} InputProps={{ disableUnderline: true }}
              />
            </Grid>
            {actualOrderData.executedQuantity > 0 && (
              <Grid item xs={3}>
                <TextField
                  fullWidth variant="filled" label="Executed Quantity" value={formatNumberWithCommas(actualOrderData.executedQuantity)} disabled sx={inputStyle} InputProps={{ disableUnderline: true }}
                />
              </Grid>
            )}
            {actualOrderData.executedAveragePrice > 0 && (
              <Grid item xs={3}>
                <TextField
                  fullWidth variant="filled" label="Executed Average Price"
                  value={Number(actualOrderData.executedAveragePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  disabled sx={inputStyle} InputProps={{ disableUnderline: true }}
                />
              </Grid>
            )}
          </>
        )}

        {/* Modularized Protocol Logic */}
        <OrderProtocolSection
          actualOrderData={actualOrderData}
          errors={errors}
          isFieldEditable={isFieldEditable}
          handleChange={handleChange}
          clearFieldError={clearFieldError}
          clearModalError={clearModalError}
          setActualOrderData={setActualOrderData}
          readOnly={readOnly}
          authToken={jwtToken || token}
          fieldOptions={fieldOptions}
          filterVenues={filterVenues}
          handleVenueChange={handleVenueChange}
          settlementDate={actualOrderData.settlementDate}
          formatSettlementDate={formatSettlementDate}
          paramLoading={paramLoading}
          paramError={paramError}
          handleStrategyParamChange={handleStrategyParamChange}
          editRfqMode={actualOrderData.editRfqMode}
        />

        {/* Modularized Comments */}
        <OrderComments
          comments={actualOrderData.comments}
          handleChange={handleChange}
          isFieldEditable={isFieldEditable}
        />

      </Grid>

      {isLadderLevelSendToMarket && (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            alignItems: "center",
            borderRadius: "8px",
            backgroundColor: "rgba(255, 193, 7, 0.16)",
            color: "#8a6d1d",
            border: "1px solid rgba(255, 193, 7, 0.45)",
            "& .MuiAlert-icon": {
              color: "#b78103",
            },
            "& .MuiAlert-message": {
              fontWeight: 600,
            },
          }}
        >
         All orders under this ladder will be submitted together. The pre-defined quantity and price from the staging blotter will be applied to each order.
        </Alert>
      )}

      {shouldShowMarketDataPanel && (
        <MarketDataInfoPanel
          marketData={fetchedMarketData?.top}
          depthData={fetchedMarketData?.depth}
          errorMessage={marketDataError}
          isLoading={isMarketDataLoading}
        />
      )}

      {!isLadderLevelSendToMarket && (showBondsDetails || cusipError) && (
        <BondDetailsPanel
          source={source}
          bondDetails={bondDetails}
          bondCalcResults={bondCalcResults}
          calcLoading={calcLoading}
          cusipError={cusipError}
          isLoading={isLoading}
          actualOrderData={actualOrderData}
          calcErrorMessage={calcErrorMessage || maturityMessage}
        />
      )}
    </Box>
  );
};

export default ActualOrderTab;
